import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ErrorType, manageError } from 'src/app/utils/warningMessage';
import { ServiceProviderSubjectService } from 'src/services/service-provider-subject.service';
import { ToastrImplService } from 'src/services/toastr.service';
import { fifthStepLabelMap, firstStepLabelMap, fourthStepLabelMap, secondStepLabelMap, thirdStepLabelMap } from './step-label-map';
import { DatePipe } from '@angular/common';
import { LocalStorageService } from 'src/app/local-storage.service';
import { ServiceProvidersService } from 'src/services/service-providers.service';

interface EfficiencyScaleRowDto {
  currentState: number | null;
  expectedOutcome: number | null;
}

interface FunctionalEfficiencyAssessmentScaleDto {
  id: number | null;
  subjectId?: number;
  dateOfCreation: Date;
  valuatorFirstName: string;
  valuatorLastName: string;
  providedService: any;
  dominantSupportLevel: number;
  serviceProvisionYear: number;

  toileting: EfficiencyScaleRowDto;
  personalHygiene: EfficiencyScaleRowDto;
  dressing: EfficiencyScaleRowDto;
  footwearManagement: EfficiencyScaleRowDto;
  eating: EfficiencyScaleRowDto;
  medicationManagement: EfficiencyScaleRowDto;

  publicTransportUse: EfficiencyScaleRowDto;
  publicServicesUse: EfficiencyScaleRowDto;
  moneyManagement: EfficiencyScaleRowDto;
  shopping: EfficiencyScaleRowDto;
  environmentalHygiene: EfficiencyScaleRowDto;
  workOrEducationEngagement: EfficiencyScaleRowDto;
  foodPreparation: EfficiencyScaleRowDto;
  phoneUse: EfficiencyScaleRowDto;
  spatialOrientation: EfficiencyScaleRowDto;

  ruleCompliance: EfficiencyScaleRowDto;
  communication: EfficiencyScaleRowDto;
  peerRelationship: EfficiencyScaleRowDto;
  socialRelationship: EfficiencyScaleRowDto;
  familyRelationshipMaintenance: EfficiencyScaleRowDto;
  communityActivityParticipation: EfficiencyScaleRowDto;
  riskAvoidance: EfficiencyScaleRowDto;
  aggressiveness: EfficiencyScaleRowDto;
  temporalOrientation: EfficiencyScaleRowDto;

  homeMobility: EfficiencyScaleRowDto;
  outdoorMobility: EfficiencyScaleRowDto;
}

@Component({
  selector: 'app-service-provider-functional-efficiency-assessment-scale-dialog',
  templateUrl: './service-provider-functional-efficiency-assessment-scale-dialog.component.html',
  styleUrls: ['./service-provider-functional-efficiency-assessment-scale-dialog.component.scss']
})
export class ServiceProviderFunctionalEfficiencyAssessmentScaleDialogComponent implements OnInit {
  firstStepForm!: FormGroup;
  secondStepForm: FormGroup;
  thirdStepForm: FormGroup;
  fourthStepForm: FormGroup;
  fifthStepForm: FormGroup;

  ratings: number[] = [1, 2, 3, 4];
  reinforcements: number[] = [0, 1, 2];
  id: number | null = null;
  subject: any;
  firstStepLabelMap = firstStepLabelMap;
  secondStepLabelMap = secondStepLabelMap;
  thirdStepLabelMap = thirdStepLabelMap;
  fourthStepLabelMap = fourthStepLabelMap;
  fifthStepLabelMap = fifthStepLabelMap;
  btnDisabled = false;
  showDate: any;
  currentDate = new Date();
  valuatorFirstName: any;
  valuatorLastName: any;
  providedServices: any = [];
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ServiceProviderFunctionalEfficiencyAssessmentScaleDialogComponent>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private serviceProviderSubjectService: ServiceProviderSubjectService,
    private toastr: ToastrImplService,
    private datePipe: DatePipe,
    private localStorageService: LocalStorageService,
    private serviceProvidersService: ServiceProvidersService,
  ) { 
    this.showDate = this.datePipe.transform(this.currentDate, 'yyyy-MM-dd');
    this.valuatorFirstName = JSON.parse(this.localStorageService.get('loggedUser')).firstName;
    this.valuatorLastName = JSON.parse(this.localStorageService.get('loggedUser')).lastName;

    this.subject = data?.subject;

    this.serviceProviderSubjectService.getEfficiencyScaleBySubjectIdUrl(this.subject.id).subscribe(response => {
      this.populateFormsFromDto(response);
    });

    this.serviceProvidersService.findProvidedServicesByServiceProviderId(this.subject.serviceProviderId).subscribe(res => {
      this.providedServices = res;
    });

    this.firstStepForm = this.fb.group({
      dateOfCreation: [this.showDate, []],
      subjectCode: [this.subject.id, []],
      dominantSupportLevel: [null, [Validators.required]],
      firstName: [this.subject.firstName, []],
      lastName: [this.subject.lastName, []],
      parentName: [this.subject.parentName, []],
      gender: [this.subject.gender, []],
      dateOfBirth: [this.subject.dateOfBirth, []],
      placeOfBirth: [this.subject.placeOfBirth, []],
      providedService: [null, [Validators.required]],
      serviceProvisionYear: ['', [Validators.required]],
      serviceProvider: [this.subject.serviceProviderName, []],
    });

    this.secondStepForm = this.fb.group({
      toileting: this.createRow('toileting'),
      personalHygiene: this.createRow('personalHygiene'),
      dressing: this.createRow('dressing'),
      footwearManagement: this.createRow('footwearManagement'),
      eating: this.createRow('eating'),
      medicationManagement: this.createRow('medicationManagement')
    });

    this.thirdStepForm = this.fb.group({
      publicTransportUse: this.createRow('publicTransportUse'),
      publicServicesUse: this.createRow('publicServicesUse'),
      moneyManagement: this.createRow('moneyManagement'),
      shopping: this.createRow('shopping'),
      environmentalHygiene: this.createRow('environmentalHygiene'),
      workOrEducationEngagement: this.createRow('workOrEducationEngagement'),
      foodPreparation: this.createRow('foodPreparation'),
      phoneUse: this.createRow('phoneUse'),
      spatialOrientation: this.createRow('spatialOrientation')
    });

    this.fourthStepForm = this.fb.group({
      ruleCompliance: this.createRow('ruleCompliance'),
      communication: this.createRow('communication'),
      peerRelationship: this.createRow('peerRelationship'),
      socialRelationship: this.createRow('socialRelationship'),
      familyRelationshipMaintenance: this.createRow('familyRelationshipMaintenance'),
      communityActivityParticipation: this.createRow('communityActivityParticipation'),
      riskAvoidance: this.createRow('riskAvoidance'),
      aggressiveness: this.createRow('aggressiveness'),
      temporalOrientation: this.createRow('temporalOrientation')
    });

    this.fifthStepForm = this.fb.group({
      homeMobility: this.createRow('homeMobility'),
      outdoorMobility: this.createRow('outdoorMobility')
    });
  }

  ngOnInit(): void {}

  private createRow(rowType: string): FormGroup {
    return this.fb.group({
      currentState: [null],
      expectedOutcome: [null],
      type: [rowType],
    });
  }  

  closeDialog(): void {
    this.dialogRef.close({ event: 'Cancel' });
  }

  compareObjects(o1: any, o2: any): boolean{
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  private mapFormGroup(form: FormGroup): Record<string, EfficiencyScaleRowDto> {
    const result: Record<string, EfficiencyScaleRowDto> = {};

    Object.keys(form.controls).forEach(key => {
       const control = form.get(key) as FormGroup;
       result[key] = {
          currentState: control.get('currentState')?.value,
          expectedOutcome: control.get('expectedOutcome')?.value,
       };
    });

    return result;
  }

  buildAssessmentScaleDto(): FunctionalEfficiencyAssessmentScaleDto {
    return {
      id: this.id,
      subjectId: this.subject.id,
      dateOfCreation: this.firstStepForm.value.dateOfCreation,
      valuatorFirstName: this.valuatorFirstName,
      valuatorLastName: this.valuatorLastName,
      dominantSupportLevel: this.firstStepForm.value.dominantSupportLevel,
      providedService: this.firstStepForm.value.providedService,
      serviceProvisionYear: this.firstStepForm.value.serviceProvisionYear,
      ...this.mapFormGroup(this.secondStepForm),
      ...this.mapFormGroup(this.thirdStepForm),
      ...this.mapFormGroup(this.fourthStepForm),
      ...this.mapFormGroup(this.fifthStepForm),
    } as FunctionalEfficiencyAssessmentScaleDto;
  }

  preview(){        
    this.serviceProviderSubjectService.previewEfficiencyScaleForm(this.buildAssessmentScaleDto());
  }

  generateReport(): void {
    this.btnDisabled = true;
    this.serviceProviderSubjectService.generateFunctionalEfficiencyAssessmentScale(this.buildAssessmentScaleDto()).subscribe(
      response => {
        this.btnDisabled = false;
        this.dialogRef.close({ event: 'Save' });
        this.toastr.success('SERVICE_PROVIDERS.FUNCTIONAL_EFFICIENCY_ASSESSMENT_SCALE.GENERATE_SUCCESS');
      },
      error => {
        manageError(this.toastr, ErrorType.SAVE_DATA);
        this.btnDisabled = false;
      }
    )
  }

  saveEfficiencyScale(): void {
    this.btnDisabled = true;
    this.serviceProviderSubjectService.saveFunctionalEfficiencyAssessmentScale(this.buildAssessmentScaleDto()).subscribe(
      response => {
        this.id = response;
        this.btnDisabled = false;
        this.toastr.success('NSP_MOCK.SAVED_DATA');
      },
      error => {
        manageError(this.toastr, ErrorType.SAVE_DATA);
        this.btnDisabled = false;
      }
    )
  }

  private applyDtoToFormGroup(form: FormGroup, dto: Partial<Record<string, EfficiencyScaleRowDto>>): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key) as FormGroup;
      const row = dto?.[key];

      if (!row) return;

      control.patchValue(
        {
          currentState: row.currentState ?? null,
          expectedOutcome: row.expectedOutcome ?? null,
        },
        { emitEvent: false }
      );
    });
  }

  private populateFormsFromDto(dto: FunctionalEfficiencyAssessmentScaleDto): void {
    if (dto == null) {
      return;
    }
    
    this.firstStepForm.patchValue({
      dateOfCreation: dto.dateOfCreation,
      dominantSupportLevel: dto.dominantSupportLevel,
      providedService: dto.providedService,
      serviceProvisionYear: dto.serviceProvisionYear
    });

    this.applyDtoToFormGroup(this.secondStepForm, dto as any);
    this.applyDtoToFormGroup(this.thirdStepForm, dto as any);
    this.applyDtoToFormGroup(this.fourthStepForm, dto as any);
    this.applyDtoToFormGroup(this.fifthStepForm, dto as any);

    this.firstStepForm.markAsPristine();
    this.secondStepForm.markAsPristine();
    this.thirdStepForm.markAsPristine();
    this.fourthStepForm.markAsPristine();
    this.fifthStepForm.markAsPristine();
    this.id = dto.id;
  }

}
